import useReveal from "../hooks/useReveal";
import { ArrowRight } from "lucide-react";

const SOLUTIONS_IMG =
    "https://static.prod-images.emergentagent.com/jobs/2da4906e-da56-4c5d-bd4c-278bf8ff8295/images/4d95bd61613c5bce5a7030d0b90d4a5e7c8f919410f6aad6631cc3171909274f.png";

const PHASES = [
    {
        id: "before",
        eyebrow: "Phase 01 · Before Shipment",
        title: "Plan freight with intent.",
        body: "Compare options, understand true freight cost and make informed procurement decisions before a single container is booked.",
        bullets: [
            "Carrier and routing insights",
            "Procurement benchmarking",
            "Rate scenario modelling",
            "Origin/destination cost preview",
        ],
    },
    {
        id: "during",
        eyebrow: "Phase 02 · During Shipment",
        title: "Stay in control end-to-end.",
        body: "Improve visibility, monitor exceptions and stay ahead of movement, timing and risk — across every leg of the journey.",
        bullets: [
            "Live shipment status",
            "Exception alerts & SLA monitoring",
            "Detention/demurrage early warning",
            "Single view across forwarders",
        ],
    },
    {
        id: "after",
        eyebrow: "Phase 03 · After Shipment",
        title: "Recover value & improve.",
        body: "Audit invoices, identify cost leakage, measure carrier performance and turn every shipment into a learning loop.",
        bullets: [
            "Invoice & freight cost audit",
            "Carrier scorecards",
            "Cost-leakage recovery",
            "Continuous improvement cycle",
        ],
    },
];

export default function Solutions() {
    const [ref, visible] = useReveal();
    return (
        <section
            id="solutions"
            data-testid="solutions-section"
            className="relative py-20 sm:py-28 lg:py-36 border-t border-white/5 overflow-hidden"
        >
            <img
                src={SOLUTIONS_IMG}
                alt=""
                aria-hidden
                className="absolute right-0 top-1/3 -translate-y-1/2 w-[60%] opacity-30 pointer-events-none mix-blend-screen"
            />
            <div className="absolute inset-0 grid-backdrop opacity-40 pointer-events-none" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                <div ref={ref} className={`reveal ${visible ? "is-visible" : ""}`}>
                    <div className="max-w-3xl mb-16">
                        <span className="section-tag" data-testid="solutions-tag">
                            Solutions · 03
                        </span>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mt-5 leading-tight">
                            Control across <span className="text-glow-cyan">three phases</span> of every shipment.
                        </h2>
                        <p className="text-zinc-400 mt-5 leading-relaxed">
                            One operating model — applied before the container moves, while
                            it&apos;s moving, and after it arrives. That&apos;s how we close the gap
                            between cost, visibility and decision-making.
                        </p>
                    </div>

                    <div className="space-y-4">
                        {PHASES.map((p, idx) => (
                            <PhaseRow key={p.id} index={idx} {...p} />
                        ))}
                    </div>

                    <div className="mt-14 flex flex-wrap items-center gap-4">
                        <a href="#early-access" className="btn-primary" data-testid="solutions-cta">
                            Get early access
                            <ArrowRight size={18} />
                        </a>
                        <span className="font-mono-accent text-[11px] text-zinc-500">
                            Product platform · coming soon
                        </span>
                    </div>
                </div>
            </div>
        </section>
    );
}

function PhaseRow({ index, id, eyebrow, title, body, bullets }) {
    return (
        <div
            data-testid={`solutions-phase-${id}`}
            className="card-glow rounded-2xl p-6 sm:p-8 lg:p-10 grid lg:grid-cols-12 gap-6 lg:gap-10"
        >
            <div className="lg:col-span-1 flex lg:flex-col items-start gap-3">
                <span
                    className="font-display text-5xl sm:text-6xl"
                    style={{
                        background: "linear-gradient(180deg, #00F0FF, rgba(0,240,255,0.25))",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                    }}
                >
                    {String(index + 1).padStart(2, "0")}
                </span>
            </div>

            <div className="lg:col-span-5">
                <div className="eyebrow">{eyebrow}</div>
                <h3 className="font-display text-2xl sm:text-3xl text-white mt-3 tracking-tight">
                    {title}
                </h3>
                <p className="text-zinc-400 mt-4 leading-relaxed">{body}</p>
            </div>

            <div className="lg:col-span-6">
                <ul className="grid sm:grid-cols-2 gap-3">
                    {bullets.map((b) => (
                        <li
                            key={b}
                            className="flex items-start gap-3 p-3 rounded-lg border border-white/5 bg-white/[0.02]"
                            data-testid={`phase-${id}-bullet`}
                        >
                            <span
                                className="mt-1.5 w-1.5 h-1.5 rounded-full"
                                style={{ background: "#00F0FF", boxShadow: "0 0 8px #00F0FF" }}
                            />
                            <span className="text-sm text-zinc-300">{b}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}
